import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, Star, Users, MapPin, ArrowRight } from 'lucide-react';

interface Tour {
  id: string;
  name: string;
  slug: string;
  short_description: string;
  main_image_url: string;
  adult_price: number;
  child_price?: number;
  base_price?: number;
  duration: string;
  max_capacity: number;
  location?: string;
  is_featured: boolean;
  pricing_type: string;
  features?: string[];
  tour_categories?: {
    name: string;
  };
}

interface TourCardProps {
  tour: Tour;
}

export const TourCard = ({ tour }: TourCardProps) => {
  return (
    <Link to={`/tour/${tour.slug}`} className="group">
      <Card className="overflow-hidden hover:shadow-2xl transition-all duration-500 group-hover:scale-105 h-full border-0 shadow-lg">
        <div className="aspect-[4/3] overflow-hidden relative">
          <img
            src={tour.main_image_url || '/placeholder.svg'}
            alt={tour.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/30 to-transparent" />
          
          {/* Price Badge - Top Right */}
          <div className="absolute top-4 right-4 bg-secondary text-secondary-foreground px-6 py-3 rounded-full shadow-lg">
            <div className="text-center">
              <p className="text-xs font-medium uppercase">From</p>
              <p className="text-2xl font-bold">${tour.pricing_type === 'package' ? tour.base_price || 0 : tour.adult_price}</p>
            </div>
          </div>

          {/* Category Badge - Top Left */}
          {tour.tour_categories && (
            <Badge className="absolute top-4 left-4 bg-accent hover:bg-accent/90 text-accent-foreground">
              {tour.tour_categories.name}
            </Badge>
          )}

          {/* Featured Badge */}
          {tour.is_featured && (
            <Badge className="absolute top-16 left-4 bg-primary hover:bg-primary/90">
              Featured
            </Badge>
          )}

          {/* Location - Bottom Left */}
          {tour.location && (
            <div className="absolute bottom-4 left-4 flex items-center gap-2 text-white bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-full">
              <MapPin className="w-4 h-4" />
              <span className="text-sm font-medium">{tour.location}</span>
            </div>
          )}
        </div>

        <CardContent className="p-6 bg-gradient-to-br from-background to-muted/20">
          <div className="mb-4">
            <h3 className="text-xl font-bold mb-2 group-hover:text-secondary transition-colors line-clamp-2 leading-tight">
              {tour.name}
            </h3>
            {tour.short_description && (
              <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                {tour.short_description}
              </p>
            )}
          </div>

          {/* Tour Details */}
          <div className="grid grid-cols-3 gap-3 mb-4 pb-4 border-b">
            <div className="flex flex-col items-center text-center">
              <Clock className="w-5 h-5 text-accent mb-1" />
              <span className="text-xs text-muted-foreground">{tour.duration}</span>
            </div>
            <div className="flex flex-col items-center text-center border-x">
              <Users className="w-5 h-5 text-accent mb-1" />
              <span className="text-xs text-muted-foreground">Max {tour.max_capacity}</span>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="flex items-center gap-1 mb-1">
                <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              </div>
              <span className="text-xs text-muted-foreground font-medium">4.9/5</span>
            </div>
          </div>

          {/* Features */}
          {tour.features && tour.features.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {tour.features.slice(0, 2).map((feature, index) => (
                <Badge key={index} variant="secondary" className="text-xs bg-accent/10 text-accent border-0">
                  {feature}
                </Badge>
              ))}
              {tour.features.length > 2 && (
                <Badge variant="secondary" className="text-xs bg-muted">
                  +{tour.features.length - 2} more
                </Badge>
              )}
            </div>
          )}

          {/* CTA Button */}
          <Badge className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground py-2 text-center justify-center cursor-pointer group-hover:shadow-lg transition-all">
            <span className="font-semibold">View Details</span>
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Badge>
        </CardContent>
      </Card>
    </Link>
  );
};